/**
 * PIPELINE UTILS — funzioni pure sui dati di pipeline/stati, senza React nè store.
 *
 * Stanno qui le regole di lettura che altrimenti si riscriverebbero identiche in ogni
 * consumatore (board, form e detail del preventivo): come si etichetta uno stato, come si
 * risale dalla FK `currentStateId` alla pipeline che lo possiede, in che ordine si mostrano
 * gli stati. Sono decisioni di dominio, non di presentazione: se cambiano, devono cambiare
 * in un punto solo.
 */

import type { Pipeline, PipelineState } from './pipelineMappers';

/**
 * Etichetta di uno stato: la descrizione se valorizzata, altrimenti il codice.
 * La descrizione è opzionale e può arrivare come stringa di soli spazi, quindi il test è
 * sul `trim()` e non sulla sola presenza.
 */
export function pipelineStateLabel(state: PipelineState): string {
  return state.description?.trim() ? state.description : state.code;
}

/**
 * Pipeline che possiede uno stato, cercata per `pipelineStateId`, o `undefined` se nessuna lo
 * contiene (stato di un'altra pipeline non caricata, o `stateId` a 0 = non assegnato).
 *
 * Il preventivo porta solo la FK `currentStateId`: la pipeline di appartenenza non è un campo
 * suo, si deduce da quale pipeline contiene quello stato. Serve sia per pre-selezionare la
 * combo pipeline in modifica, sia per sapere entro quali stati e' lecito muoversi.
 */
export function findOwningPipeline(
  pipelines: Pipeline[],
  stateId: number,
): Pipeline | undefined {
  if (stateId <= 0) return undefined;
  return pipelines.find((p) => p.states.some((s) => s.pipelineStateId === stateId));
}

/**
 * Stati di una pipeline nell'ordine del funnel (`position` crescente), su una copia: l'array
 * arriva dallo store Redux e `sort()` muta in place. Pipeline assente/non caricata -> array vuoto.
 *
 * L'ordine e' significativo ovunque si mostrino gli stati — colonne della board, opzioni della
 * combo — e nessuna delle sorgenti garantisce che arrivino gia' ordinati.
 */
export function sortedStates(pipeline: Pipeline | undefined): PipelineState[] {
  if (!pipeline) return [];
  return [...pipeline.states].sort((a, b) => a.position - b.position);
}
